// QR Kiosk v2 — Company details (surface 4). v1 CompanyDetailsPage parity +
// scoping. Owner company of machine.customerId. Fixes: single Address row (PG-9),
// website dual transform (PG-8), resilient back bar in EVERY branch (v1 strand).
// Contact order Phone → Email → Address → Website. Tokens only.
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachine, getCustomer } from "../../publicSlice";
import { KPage } from "../shared/KPage";
import { Glyph, GD } from "../shared/glyphs";
import { ErrorCallout } from "../shared/ErrorCallout";
import { CachedErrorState } from "../shared/CachedErrorState";
import { useSurfaceState } from "../shared/useSurfaceState";
import { useKioskRouteMachine } from "../shared/useKioskRouteMachine";
import { formatClockTime } from "../shared/clock";
import { SkeletonBlock } from "../shared/SkeletonBlocks";
import { usePressed } from "../shared/usePressed";

const cardStyle: React.CSSProperties = {
  margin: "12px 14px 0",
  border: "1px solid var(--border)",
  borderRadius: 12,
  boxShadow: "var(--shadow)",
  background: "var(--card)",
  overflow: "hidden",
  flexShrink: 0,
};

interface ContactRowProps {
  glyph: string;
  label: string;
  value: string;
  valueColor: string;
  href?: string;
  external?: boolean;
}

function ContactRow({ glyph, label, value, valueColor, href, external }: ContactRowProps) {
  const press = usePressed();
  const actionable = !!href;

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minHeight: 56,
    padding: "8px 14px",
    borderTop: "1px solid var(--border-soft)",
  };

  const inner = (
    <>
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          background: "var(--tier-accent-soft)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--tier-accent)",
          flexShrink: 0,
        }}
      >
        <Glyph d={glyph} size={17} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="eyebrow" style={{ color: "var(--faint)", marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.4, color: valueColor, overflowWrap: "anywhere" }}>
          {value}
        </div>
      </div>
      {actionable && (
        <span style={{ color: "var(--faint)", flexShrink: 0 }}>
          <Glyph d={GD.chev} size={15} />
        </span>
      )}
    </>
  );

  if (!actionable) {
    return <div style={rowStyle}>{inner}</div>;
  }
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      {...press.pressHandlers}
      style={{ ...rowStyle, textDecoration: "none", color: "inherit", ...press.pressStyle }}
    >
      {inner}
    </a>
  );
}

function CompanySkeleton() {
  return (
    <div style={{ ...cardStyle, boxShadow: "none" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "16px 14px" }}>
        <SkeletonBlock w={52} h={52} r={11} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <SkeletonBlock w="50%" h={14} />
          <SkeletonBlock w="30%" h={9} style={{ marginTop: 5 }} />
        </div>
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            minHeight: 56,
            padding: "8px 14px",
            borderTop: "1px solid var(--border-soft)",
          }}
        >
          <SkeletonBlock w={36} h={36} r={9} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <SkeletonBlock w="25%" h={8} />
            <SkeletonBlock w="60%" h={11} style={{ marginTop: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CompanyV2() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const { customer } = useAppSelector((s) => s.public);

  useEffect(() => {
    if (id) dispatch(getMachine(id));
  }, [id, dispatch]);

  const m = useKioskRouteMachine(id); // KV2-1-e route-scope via shared resolver (guest-curated backfill)

  // KV2-5: pattern-B local CustStatus removed — the customer surface is validated by
  // the slice loadState + the committed data-level guard `c` below.
  const fetchCustomer = (customerId: string) => dispatch(getCustomer(customerId));
  useEffect(() => {
    if (m?.customerId) dispatch(getCustomer(m.customerId));
  }, [m?.customerId, dispatch]);

  const stMachine = useSurfaceState("machine", id);
  const stCust = useSurfaceState("customer", m?.customerId);

  // Data-level scope guard — the store's customer may belong to another machine's owner.
  const c = customer && m && customer.id === m.customerId ? customer : undefined;

  // §5 tree over machine (context) + customer (body). BUG-1: skeleton covers idle+pending.
  let body: React.ReactNode;
  if (!m) {
    body = stMachine.failed ? (
      <ErrorCallout
        title={translate("kioskV2DashboardLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => id && dispatch(getMachine(id))}
        retryLabel={translate("kioskV2Retry")}
      />
    ) : (
      <CompanySkeleton />
    );
  } else if (!c) {
    body = stCust.failed ? (
      <ErrorCallout
        title={translate("kioskV2CompanyLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => fetchCustomer(m.customerId)}
        retryLabel={translate("kioskV2Retry")}
      />
    ) : (
      <CompanySkeleton />
    );
  } else {
    const address = [c.address?.addressLine, c.address?.zipCode, c.address?.city, c.address?.countryName]
      .filter(Boolean)
      .join(", ");
    const websiteDisplay = c.website ? c.website.replace(/^https?:\/\//, "") : "";
    const websiteHref = c.website ? (c.website.startsWith("http") ? c.website : `https://${c.website}`) : "";

    const content = (
      <div className="kv2-enter" style={cardStyle}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "16px 14px" }}>
          <div style={{ width: 52, height: 52, borderRadius: 11, flexShrink: 0, overflow: "hidden" }}>
            {c.logo?.content ? (
              <img
                src={c.logo.content}
                alt={c.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "var(--ink)",
                  color: "var(--card)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15,
                  fontWeight: 800,
                }}
              >
                {(c.name || "").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div
            style={{
              minWidth: 0,
              fontSize: 17,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--ink)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {c.name}
          </div>
        </div>

        {/* Contact rows — designer order Phone → Email → Address → Website */}
        {c.phone && (
          <ContactRow
            glyph={GD.phone}
            label={translate("companyPhone")}
            value={c.phone}
            valueColor="var(--tier-accent)"
            href={`tel:${c.phone}`}
          />
        )}
        {c.email && (
          <ContactRow
            glyph={GD.mail}
            label={translate("companyEmail")}
            value={c.email}
            valueColor="var(--tier-accent)"
            href={`mailto:${c.email}`}
          />
        )}
        {address && (
          <ContactRow glyph={GD.bldg} label={translate("companyAddress")} value={address} valueColor="var(--ink)" />
        )}
        {c.website && (
          <ContactRow
            glyph={GD.out}
            label={translate("website")}
            value={websiteDisplay}
            valueColor="var(--tier-accent)"
            href={websiteHref}
            external
          />
        )}
      </div>
    );

    const machineFailed = stMachine.failed; // outer surface wins the callout
    if (machineFailed || stCust.failed) {
      body = (
        <CachedErrorState
          title={machineFailed ? translate("kioskV2DashboardLoadFailedTitle") : translate("kioskV2CompanyLoadFailedTitle")}
          hint={translate("kioskV2DashboardLoadFailedHint")}
          onRetry={machineFailed ? () => id && dispatch(getMachine(id)) : () => fetchCustomer(m.customerId)}
          retryLabel={translate("kioskV2Retry")}
          cachedAt={stCust.loadedAt}
          cachedLabel={stCust.loadedAt != null ? translate("kioskV2ShowingCachedFrom", { time: formatClockTime(stCust.loadedAt) }) : undefined}
        >
          {content}
        </CachedErrorState>
      );
    } else {
      body = content;
    }
  }

  return (
    <KPage
      titleKey="kioskCompanyDetails"
      glyph={GD.bldg}
      backNested
      backTo={id ? `/public-v2/machines/${id}/settings` : undefined}
      machineNumber={id}
    >
      {body}
    </KPage>
  );
}

export default CompanyV2;
